/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import CustomPopover from "@/app/shared/custom-popover";
import EyeIcon from "@/components/icons/eye";
import PencilIcon from "@/components/icons/pencil";
import TrashIcon from "@/components/icons/trash";
import UserSettingsIcon from "@/components/icons/user-settings";
import { HeaderCell } from "@/components/ui/table";
import { routes } from "@/config/routes";
import { SortConfig } from "@/hooks/useTableServer";
import { getParamsCurrentPage } from "@/utilities/functions";
import { IUserData, Roles } from "@/utilities/types/Users";
import { Link } from "react-router-dom";
import { ActionIcon, Button, SelectOption, Dropdown } from "rizzui";
import { PiDotsThreeVerticalBold, PiKey, PiTag } from "react-icons/pi";
import SelectDomain from "@/app/app-shared/user/users-table/select-domain.tsx";
import StatusAccount from "./status-account";
import UserRepository from "@/utilities/repositories/Users";
import { EFeatureType } from "@/utilities/types/AdvanceFeature";
import NoteIcon from "@/components/icons/note";
import { LuShieldQuestion } from "react-icons/lu";
import { getColorTagStyles } from "@/utilities/functions/getColorTagStyles";
import AdCreditBalanceCell from "./ad-credit-badge-cell";

type UserSortBy = "email" | "location";

type Columns = {
  data: IUserData[];
  sortConfig?: SortConfig<UserSortBy>;
  onOpenDeleteModal: (user: IUserData) => void;
  onHeaderCellClick?: (value: string) => void;
  onLoginAccountAgent: (email: string) => Promise<void>;
  handleReset: () => void;
  refresh?: () => void;
  onEditNote: (userId: string, note?: string) => void;
  onOpenSuspendModal: (user: IUserData) => void;
  onManageColorTags: (user: IUserData) => void;
  onOpenCreditModal: (userId: string) => void;
  onChangePassword: (userId: string, userName?: string) => void;
};

// Helper function to strip HTML tags
const stripHtml = (html: string | undefined): string => {
  if (!html) return "";
  return html.replace(/<[^>]*>?/gm, "").replace(/&nbsp;/g, " ");
};

export const getColumns = ({
  sortConfig,
  onOpenDeleteModal,
  onHeaderCellClick,
  onLoginAccountAgent,
  handleReset,
  refresh,
  onEditNote,
  onOpenSuspendModal,
  onManageColorTags,
  onOpenCreditModal,
  onChangePassword,
}: Columns) => [
  {
    title: <HeaderCell title="Name" />,

    dataIndex: "lastName",
    key: "lastName",
    render: (_: string, row: IUserData) => {
      return row.firstName + " " + row.lastName;
    },
  },

  {
    title: (
      <HeaderCell
        title="Email"
        sortable
        align="center"
        ascending={
          sortConfig?.direction === "asc" && sortConfig?.key === "email"
        }
      />
    ),
    dataIndex: "email",
    key: "email",
    align: "center",
    onHeaderCell: () => onHeaderCellClick?.("email"),
  },

  {
    title: "Domain",
    dataIndex: "domain",
    key: "domain",
    align: "center",
    render: (_: string, row: IUserData) => {
      const domain = row?.domains?.find((item) => !!item.primary);

      const isHasDirectoryPermission = !!row?.userFeature?.find(
        (item) => item?.feature?.type === EFeatureType.Directory
      )?.enabled;

      if (!isHasDirectoryPermission) return <></>;

      const mapOptions = row?.domains?.map((item) => {
        return {
          primary: item.primary,
          value: item.id,
          label: item.name,
          configID: item.configId,
        };
      });

      let defaultOptions = null;
      domain &&
        (defaultOptions = {
          primary: domain.primary,
          value: domain.id,
          label: `${domain.name}`,
          configID: domain.configId,
        });

      return (
        mapOptions?.length > 0 && (
          <SelectDomain
            options={mapOptions}
            defaultOptions={defaultOptions}
            configID={row.domains}
            reset={refresh}
            className="min-w-[120px]"
          />
        )
      );
    },
  },

  {
    title: <HeaderCell title="Status" align="center" />,
    dataIndex: "statusAccount",
    key: "statusAccount",
    align: "center",
    render: (_: string, row: IUserData) => {
      return (
        <StatusAccount
          onChange={async (value: SelectOption) => {
            await UserRepository.updateUser(row.id, {
              statusAccount: value.value as "active" | "inactive",
            });
            await refresh?.();
          }}
          status={row.statusAccount}
          className="w-full"
        />
      );
    },
  },

  {
    title: <HeaderCell title="Ad Credit Balance" align="center" />,
    dataIndex: "adCreditBalance",
    key: "adCreditBalance",
    align: "center",
    render: (_: string, row: IUserData) => {
      return (
        <AdCreditBalanceCell
          userId={row.id}
          balance={row.netBalance}
          onOpenCreditModal={onOpenCreditModal}
        />
      );
    },
  },

  // {
  //   title: (
  //     <HeaderCell
  //       title="Registered"
  //       sortable
  //       align="center"
  //       ascending={
  //         sortConfig?.direction === "asc" && sortConfig?.key === "createdAt"
  //       }
  //     />
  //   ),
  //   onHeaderCell: () => onHeaderCellClick?.("createdAt"),

  //   dataIndex: "createdAt",
  //   key: "createdAt",
  //   align: "center",
  //   render: (_: string, row: IUserData) => {
  //     return dayjs(row.createdAt).format("MMMM DD, YYYY");
  //   },
  // },

  // {
  //   title: <HeaderCell title="Promotion Code" align="center" />,
  //   onHeaderCell: () => onHeaderCellClick?.("createdAt"),

  //   dataIndex: "promotionCode",
  //   key: "promotionCode",
  //   align: "center",
  //   render: (_: string, row: IUserData) => {
  //     return row.pricingPlans === "NAVISVIP"
  //       ? "NAVISVIP"
  //       : row?.userPackage?.[0]?.xeroPayment?.promotionUsage?.promotion?.code ||
  //           "";
  //   },
  // },

  {
    title: <HeaderCell title="Login account Agent" align="center" />,

    dataIndex: "signIn",
    key: "signIn",
    align: "center",
    render: (_: string, row: IUserData) => {
      return (
        <CustomPopover
          title={`Login account Agent`}
          description={`This action will terminate agent's session. Are you sure you want to continue?`}
          onAccept={async () => {
            await onLoginAccountAgent(row.email);
          }}
        >
          <Button onClick={() => {}}>Login</Button>
        </CustomPopover>
      );
    },
  },

  {
    title: (
      <HeaderCell
        className="ps-3"
        title={<span className="whitespace-nowrap">Actions</span>}
      />
    ),
    dataIndex: "action",
    key: "action",
    width: 80,
    render: (_: string, row: IUserData) => {
      const hasNote = !!row.note;
      const hasSuspend = !!row.userSuspends?.length;

      return (
        <div className="flex items-center justify-end gap-2 pe-4">
          <Dropdown placement="bottom-end">
            <Dropdown.Trigger>
              <ActionIcon variant="outline" size="sm">
                <PiDotsThreeVerticalBold className="h-4 w-4" />
              </ActionIcon>
            </Dropdown.Trigger>
            <Dropdown.Menu className="min-w-[180px]">
              <Dropdown.Item
                className="gap-2"
                onClick={() => onOpenSuspendModal(row)}
              >
                <LuShieldQuestion
                  className={`h-4 w-4 ${hasSuspend ? "text-red-500" : ""}`}
                />
                Suspend User
              </Dropdown.Item>

              <Link
                to={{
                  pathname: routes.user.edit(row.id),
                  search: getParamsCurrentPage({ rowKey: row?.id }),
                }}
              >
                <Dropdown.Item className="gap-2">
                  <PencilIcon className="h-4 w-4" />
                  Edit User
                </Dropdown.Item>
              </Link>

              <Dropdown.Item
                className="gap-2"
                onClick={() =>
                  onChangePassword(
                    row.id,
                    `${row.firstName || ""} ${row.lastName || ""}`.trim()
                  )
                }
              >
                <PiKey className="h-4 w-4" />
                Change Password
              </Dropdown.Item>

              <Link
                to={{
                  pathname: routes.user.advanceFeature(row.id),
                  search: getParamsCurrentPage({ rowKey: row?.id }),
                }}
              >
                <Dropdown.Item className="gap-2">
                  <UserSettingsIcon className="h-4 w-4" />
                  Advance Features
                </Dropdown.Item>
              </Link>

              <Link
                to={{
                  pathname: routes.user.loginActivity(row.id),
                  search: getParamsCurrentPage({ rowKey: row?.id }),
                }}
                state={{
                  user: row,
                }}
              >
                <Dropdown.Item className="gap-2">
                  <EyeIcon className="h-4 w-4" />
                  Login Activity
                </Dropdown.Item>
              </Link>

              <Dropdown.Item
                className="gap-2"
                onClick={() => onManageColorTags(row)}
              >
                <PiTag
                  className={`h-4 w-4 ${getColorTagStyles(row.userColorTags?.[0]?.colorTag)}`}
                />
                Manage Color Tags
              </Dropdown.Item>

              <Dropdown.Item
                className="gap-2"
                onClick={() => onEditNote(row.id, row.note)}
              >
                <NoteIcon
                  className={`h-4 w-4 ${hasNote ? "text-blue-500" : ""}`}
                />
                {hasNote ? "Edit Note" : "Add Note"}
              </Dropdown.Item>

              {row.role !== Roles.ADMIN && (
                <Dropdown.Item
                  className="gap-2 text-red-500 hover:text-red-600"
                  onClick={() => onOpenDeleteModal(row)}
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete User
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      );
    },
  },
];
